import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import FocusLock from 'react-focus-lock';
import {useIntl} from 'react-intl';

import {
  Box, Button, Flex, IconButton, Skeleton, Text, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import BaseLink from '@amblique/king-living/app/components/link';

import {ArrowRightIcon, ChevronRightIcon2, HeaderCloseIcon, SearchIcon} from '@amblique/king-living/app/components/icons';
import {useMenuItemById} from '@amblique/contentful/app/contentful/hooks/use-menu-item';
import {useNavigationColumnById} from '@amblique/contentful/app/contentful/hooks/use-navigation-column';
import {Image as ContentfulImage} from '@amblique/king-living/app/contentful/models/image';
import Search from '@amblique/king-living/app/components/search';
import {NavigationPrimaryTabs} from '@amblique/king-living/app/components/header/partials/navigation-primary-tabs';
import {useHeaderContext} from '@amblique/king-living/app/contexts/header-context';
import {useEscapeToClose} from '@amblique/king-living/app/hooks/use-escape-to-close';
import {NAV_DRAWER_WIDTH, NAV_L1_HOVER_INTENT_MS, SEARCH_TAB_ID} from '@amblique/king-living/app/constants/navigation-drawer';
import {
  getColumnLinks,
  getColumnTitle,
  getFeaturedLink,
  formatShopAllLabel,
  getLinkMenuGallery,
  getLinkText,
  getLinkUrl,
  getNavLinkAnchorProps,
  getMenuItemColumns,
  getMenuItemId,
  getMenuItemImages,
  getMenuItemTitle,
  menuItemHasSubmenu,
} from '@amblique/king-living/app/utils/navigation-primary-tabs';
import {print} from 'graphql';
import {useQuery} from '@tanstack/react-query';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {getNavLinkGalleryById} from '@amblique/king-living/app/contentful/queries/navigation-column.queries';
import {ensureArray} from '@amblique/king-living/app/utils/utils';

const resolveDrawerWidth = ({isSearchTab, selectedCategory, hasGallery}) => {
  if (isSearchTab) {
    return NAV_DRAWER_WIDTH.search;
  }

  if (selectedCategory && hasGallery) {
    return NAV_DRAWER_WIDTH.gallery;
  }

  if (selectedCategory) {
    return NAV_DRAWER_WIDTH.l2;
  }

  return NAV_DRAWER_WIDTH.l1;
};

const TABBABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const tabbablesIn = (root) => {
  if (!root) {
    return [];
  }

  return [...root.querySelectorAll(TABBABLE_SELECTOR)].filter((node) => {
    if (node.getAttribute('tabindex') === '-1' || node.hasAttribute('disabled')) {
      return false;
    }

    const style = window.getComputedStyle(node);

    return style.display !== 'none' && style.visibility !== 'hidden';
  });
};

const eventTargetControl = (target) => {
  if (!(target instanceof Element)) {
    return null;
  }

  return target.closest(TABBABLE_SELECTOR);
};

const controlContains = (control, target) => Boolean(
  control && target && (control === target || control.contains(target))
);

const CategoryGallery = ({images, styles, onNavigate}) => {
  if (!images.length) {
    return null;
  }

  return (
    <Box
      className="mega-menu-drawer__gallery"
      {...styles.drawerGallery}
      sx={{
        '& > .mega-menu-drawer__gallery-item': {
          transition: 'opacity 150ms ease',
        },
        '&:hover > .mega-menu-drawer__gallery-item, &:focus-within > .mega-menu-drawer__gallery-item': {
          opacity: '0.4 !important',
        },
        '&:hover > .mega-menu-drawer__gallery-item:hover, &:focus-within > .mega-menu-drawer__gallery-item:focus-visible': {
          opacity: '1 !important',
        },
      }}
    >
      {images.map((image, index) => {
        const caption = image?.title || image?.description || '';
        const href = image?.url;
        const imageOnly = {
          ...image,
          description: undefined,
          imageDescriptionContent: undefined,
          url: undefined,
        };

        const tile = (
          <>
            <Box overflow="hidden" width="100%" {...styles.drawerGalleryImage}>
              <ContentfulImage
                component={imageOnly}
                loading={index < 4 ? 'eager' : 'lazy'}
              />
            </Box>
            {caption ? (
              <Text as="span" {...styles.drawerGalleryCaption}>{caption}</Text>
            ) : null}
          </>
        );

        if (!href) {
          return (
            <Box
              key={image?.sys?.id || caption}
              className="mega-menu-drawer__gallery-item"
              tabIndex={0}
              {...styles.drawerGalleryItem}
            >
              {tile}
            </Box>
          );
        }

        return (
          <BaseLink
            key={image?.sys?.id || caption}
            className="mega-menu-drawer__gallery-item"
            {...getNavLinkAnchorProps(image, href)}
            {...styles.drawerGalleryItem}
            onClick={onNavigate}
          >
            {tile}
          </BaseLink>
        );
      })}
    </Box>
  );
};

CategoryGallery.propTypes = {
  images: PropTypes.array,
  onNavigate: PropTypes.func,
  styles: PropTypes.object,
};

CategoryGallery.propTypes = {
  images: PropTypes.array,
  onNavigate: PropTypes.func,
  styles: PropTypes.object,
};

const GALLERY_QUERY_OPTIONS = {
  staleTime: 10 * 60 * 1000,
  gcTime: 15 * 60 * 1000,
  refetchOnWindowFocus: false,
  retry: false,
};

const useNavLinkGallery = (id, enabled) => {
  const contentfulService = useService(ContentfulService);

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'navLinkGallery',
      id,
      contentfulService?.locale,
    ],
    queryFn: async () => {
      const data = await contentfulService.query(
        print(getNavLinkGalleryById),
        {
          id,
          locale: contentfulService.locale,
          preview: false,
        }
      );

      return data.link || null;
    },
    enabled: Boolean(enabled && id && contentfulService),
    ...GALLERY_QUERY_OPTIONS,
  });
};

const DrawerL2Link = ({link, onHideGallery, onNavigate, onPreviewImages, styles}) => {
  const [isHovered, setIsHovered] = useState(false);
  const href = getLinkUrl(link);
  const label = getLinkText(link);
  const packedGallery = getLinkMenuGallery(link);
  const linkId = link?.sys?.id || '';
  const {data: galleryLink} = useNavLinkGallery(
    linkId,
    isHovered && packedGallery.length === 0
  );

  useEffect(() => {
    if (!isHovered) {
      return;
    }

    const images = packedGallery.length ? packedGallery : getLinkMenuGallery(galleryLink);

    if (images.length) {
      onPreviewImages?.(images);
      return;
    }

    onHideGallery?.();
  }, [galleryLink, isHovered, onHideGallery, onPreviewImages, packedGallery.length]);

  const previewHandlers = {
    onBlur: () => setIsHovered(false),
    onFocus: () => setIsHovered(true),
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
  };

  if (!label) {
    return null;
  }

  if (!href) {
    return (
      <Text
        as="span"
        tabIndex={0}
        data-nav-l3-gallery=""
        {...styles.drawerL2Link}
        {...previewHandlers}
      >
        {label}
      </Text>
    );
  }

  return (
    <BaseLink
      data-nav-l3-gallery=""
      {...getNavLinkAnchorProps(link, href)}
      {...styles.drawerL2Link}
      {...previewHandlers}
      onClick={onNavigate}
    >
      {label}
    </BaseLink>
  );
};

DrawerL2Link.propTypes = {
  link: PropTypes.object,
  onHideGallery: PropTypes.func,
  onNavigate: PropTypes.func,
  onPreviewImages: PropTypes.func,
  styles: PropTypes.object,
};

const DrawerColumn = ({column, onNavigate, onHideGallery, onPreviewImages, styles}) => {
  const existingLinks = getColumnLinks(column);
  const columnId = column?.sys?.id || '';
  // Links-only. Never fetch `full` (gallery on every L2 link → complexity 400, empty column).
  const shouldFetch = Boolean(columnId) && existingLinks.length === 0;
  const {data: linksColumn, isFetching} = useNavigationColumnById(columnId || null, {
    variant: 'default',
    queryOptions: {
      enabled: shouldFetch,
      ...GALLERY_QUERY_OPTIONS,
    },
  });
  const resolvedColumn = existingLinks.length ? column : (linksColumn || column);
  const title = getColumnTitle(resolvedColumn) || getColumnTitle(column);
  const links = getColumnLinks(resolvedColumn);
  const showSkeleton = existingLinks.length === 0 && isFetching && !linksColumn;

  return (
    <Box {...styles.drawerL2Column}>
      {title ? (
        <Text as="h3" {...styles.drawerL2ColumnTitle}>{title}</Text>
      ) : null}
      {showSkeleton ?
        [0, 1, 2, 3].map((index) => (
          <Skeleton key={`${columnId}-skeleton-${index}`} height="20px" width="72%" />
        )) :
        (
          <Box {...styles.drawerL2LinkGroup}>
            {links.map((link) => (
              <DrawerL2Link
                key={link?.sys?.id || getLinkUrl(link) || getLinkText(link)}
                link={link}
                onHideGallery={onHideGallery}
                onNavigate={onNavigate}
                onPreviewImages={onPreviewImages}
                styles={styles}
              />
            ))}
          </Box>
        )
      }
    </Box>
  );
};

DrawerColumn.propTypes = {
  column: PropTypes.object,
  onHideGallery: PropTypes.func,
  onNavigate: PropTypes.func,
  onPreviewImages: PropTypes.func,
  styles: PropTypes.object,
};

const LevelTwoColumns = ({
  category,
  categoryImages,
  onHideGallery,
  onNavigate,
  onPreviewImages,
  styles,
}) => {
  const intl = useIntl();
  const featuredLink = getFeaturedLink(category);
  const featuredHref = getLinkUrl(featuredLink);
  const featuredLabel = formatShopAllLabel(category, intl.formatMessage);
  const columns = getMenuItemColumns(category);

  const previewCategoryGallery = () => {
    if (!categoryImages?.length) {
      return;
    }

    onPreviewImages?.(categoryImages);
  };

  const shopAllPreviewHandlers = {
    onFocus: previewCategoryGallery,
    onMouseEnter: previewCategoryGallery,
  };

  const shopAllIcon = (
    <Box
      as="span"
      aria-hidden
      display="inline-flex"
      alignItems="center"
      flexShrink={0}
      h="16px"
      w="16px"
    >
      <ArrowRightIcon boxSize="16px" height="16px" width="16px" />
    </Box>
  );

  const shopAllLabel = (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      fontFamily="body"
      fontSize="20px"
      fontWeight={500}
      letterSpacing={0}
      lineHeight="1.2"
      sx={{fontFeatureSettings: "'lnum' 1, 'pnum' 1"}}
    >
      {featuredLabel}
    </Box>
  );

  return (
    <Box
      className="mega-menu-drawer__l2"
      data-nav-l2=""
      {...styles.drawerL2}
      onFocusCapture={(event) => {
        if (event.target.closest('[data-nav-shop-all]')) {
          previewCategoryGallery();
        }
      }}
    >
      {featuredLabel ? (
        featuredHref ? (
          <BaseLink
            className="mega-menu-drawer__shop-all"
            data-nav-shop-all=""
            {...getNavLinkAnchorProps(featuredLink, featuredHref)}
            {...styles.drawerShopAll}
            {...shopAllPreviewHandlers}
            onClick={onNavigate}
          >
            {shopAllLabel}
            {shopAllIcon}
          </BaseLink>
        ) : (
          <Box
            as="button"
            className="mega-menu-drawer__shop-all"
            data-nav-shop-all=""
            type="button"
            {...styles.drawerShopAll}
            {...shopAllPreviewHandlers}
          >
            {shopAllLabel}
            {shopAllIcon}
          </Box>
        )
      ) : null}
      {columns.map((column) => (
        <DrawerColumn
          key={column?.sys?.id || getColumnTitle(column)}
          column={column}
          onHideGallery={onHideGallery}
          onNavigate={onNavigate}
          onPreviewImages={onPreviewImages}
          styles={styles}
        />
      ))}
    </Box>
  );
};

LevelTwoColumns.propTypes = {
  category: PropTypes.object,
  categoryImages: PropTypes.array,
  onHideGallery: PropTypes.func,
  onNavigate: PropTypes.func,
  onPreviewImages: PropTypes.func,
  styles: PropTypes.object,
};

/**
 * Click-to-open desktop navigation drawer.
 * Click a top tab to open. Inside Shop, hover (with intent delay) or keyboard
 * focus on a category opens L2. Tab then walks that category's L2/L3 before
 * the next L1 item. Hover/focus Shop all uses MenuItem.images; hover/focus a
 * column Link uses that Link's Menu Image Gallery. Opening L2 alone does not
 * show images.
 */
export const MegaMenuDrawer = ({isLoading = false, primaryTabs = [], ...props}) => {
  const intl = useIntl();
  const hoverIntentTimerRef = useRef(null);
  const galleryFromRef = useRef(null);
  const wasOpenRef = useRef(false);
  const {
    closeNavDrawer,
    navDrawerTabId,
    openNavDrawer,
    selectedCategoryId,
    setSelectedCategoryId,
  } = useHeaderContext();
  const styles = useMultiStyleConfig('MegaMenu', {isDrawerOpen: Boolean(navDrawerTabId)});
  const isSearchTab = navDrawerTabId === SEARCH_TAB_ID;
  const isOpen = Boolean(navDrawerTabId);
  const [hoveredGalleryImages, setHoveredGalleryImages] = useState(null);
  const [isGalleryPreview, setIsGalleryPreview] = useState(false);
  const [staggerL1, setStaggerL1] = useState(false);

  const activeTab = useMemo(
    () => primaryTabs.find((tab) => tab.id === navDrawerTabId) || null,
    [navDrawerTabId, primaryTabs]
  );

  const categories = ensureArray(activeTab?.menuItems);
  const promotionalLinks = ensureArray(activeTab?.promotionalLinks);
  const selectedSummary = categories.find((item) => getMenuItemId(item) === selectedCategoryId) || null;
  const {data: fullCategory} = useMenuItemById(selectedCategoryId || null, {
    variant: 'full',
    queryOptions: {
      enabled: Boolean(selectedCategoryId),
      retry: false,
    },
  });
  const selectedCategory = fullCategory || selectedSummary;
  const categoryImages = getMenuItemImages(selectedCategory);
  const galleryImages = isGalleryPreview
    ? (hoveredGalleryImages?.length ? hoveredGalleryImages : categoryImages)
    : [];
  const drawerWidth = resolveDrawerWidth({
    hasGallery: galleryImages.length > 0,
    isSearchTab,
    selectedCategory,
  });

  const handleNavigate = useCallback(() => {
    closeNavDrawer();
  }, [closeNavDrawer]);

  const handleTabClick = useCallback((tabId) => {
    openNavDrawer(tabId);
  }, [openNavDrawer]);

  const selectCategory = useCallback((menuItem) => {
    const id = getMenuItemId(menuItem);

    if (id) {
      setSelectedCategoryId(id);
    }
  }, [setSelectedCategoryId]);

  const clearHoverIntent = useCallback(() => {
    if (hoverIntentTimerRef.current) {
      clearTimeout(hoverIntentTimerRef.current);
      hoverIntentTimerRef.current = null;
    }
  }, []);

  const scheduleSelectCategory = useCallback((menuItem) => {
    clearHoverIntent();
    hoverIntentTimerRef.current = setTimeout(() => {
      selectCategory(menuItem);
    }, NAV_L1_HOVER_INTENT_MS);
  }, [clearHoverIntent, selectCategory]);

  const handleCategoryClick = useCallback((menuItem, event) => {
    if (getLinkUrl(getFeaturedLink(menuItem))) {
      closeNavDrawer();

      return;
    }

    if (menuItemHasSubmenu(menuItem) || getMenuItemImages(menuItem).length > 0) {
      event?.preventDefault();
      clearHoverIntent();
      selectCategory(menuItem);
    }
  }, [clearHoverIntent, closeNavDrawer, selectCategory]);

  const handleCategoryMouseEnter = useCallback((menuItem) => {
    if (menuItemHasSubmenu(menuItem) || getMenuItemImages(menuItem).length > 0) {
      scheduleSelectCategory(menuItem);
    }
  }, [scheduleSelectCategory]);

  const handleCategoryMouseLeave = useCallback(() => {
    clearHoverIntent();
  }, [clearHoverIntent]);

  const handleCategoryFocus = useCallback((menuItem) => {
    if (menuItemHasSubmenu(menuItem) || getMenuItemImages(menuItem).length > 0) {
      clearHoverIntent();
      selectCategory(menuItem);
    }
  }, [clearHoverIntent, selectCategory]);

  const handleDrawerTabOrder = useCallback((event) => {
    if (event.key !== 'Tab') {
      return;
    }

    const root = event.currentTarget;
    const active = eventTargetControl(event.target);
    const chromeTabs = tabbablesIn(root.querySelector('[data-nav-chrome]'));
    const firstChrome = chromeTabs[0];
    const promoTabs = tabbablesIn(root.querySelector('.mega-menu-drawer__promo'));
    const firstPromo = promoTabs[0];
    const lastPromo = promoTabs[promoTabs.length - 1];
    const columnTabs = tabbablesIn(root.querySelector('[data-nav-l2]'));
    const galleryTabs = tabbablesIn(root.querySelector('.mega-menu-drawer__gallery'));
    const firstL2 = columnTabs[0];
    const lastColumn = columnTabs[columnTabs.length - 1];
    const firstGallery = galleryTabs[0];
    const lastGallery = galleryTabs[galleryTabs.length - 1];
    const lastL2 = lastGallery || lastColumn;
    const selectedL1 = selectedCategoryId
      ? root.querySelector(`[data-nav-l1-id="${CSS.escape(selectedCategoryId)}"]`)
      : null;
    const selectedIndex = categories.findIndex((item) => getMenuItemId(item) === selectedCategoryId);
    const nextCategory = selectedIndex >= 0 ? categories[selectedIndex + 1] : null;
    const nextL1Id = nextCategory ? getMenuItemId(nextCategory) : '';
    const nextL1 = nextL1Id
      ? root.querySelector(`[data-nav-l1-id="${CSS.escape(nextL1Id)}"]`)
      : null;

    const moveFocus = (node) => {
      if (!node) {
        return false;
      }

      event.preventDefault();
      event.stopPropagation();
      node.focus();

      return true;
    };

    if (!event.shiftKey && lastPromo && controlContains(lastPromo, active)) {
      moveFocus(firstChrome);

      return;
    }

    if (event.shiftKey && controlContains(firstChrome, active)) {
      moveFocus(lastPromo || lastL2);

      return;
    }

    if (!firstL2) {
      return;
    }

    if (!event.shiftKey && controlContains(selectedL1, active)) {
      moveFocus(firstL2);

      return;
    }

    const l3Trigger = active instanceof Element
      ? active.closest('[data-nav-shop-all], [data-nav-l3-gallery]')
      : null;

    if (!event.shiftKey && firstGallery && l3Trigger) {
      galleryFromRef.current = l3Trigger;
      moveFocus(firstGallery);

      return;
    }

    if (event.shiftKey && firstGallery && controlContains(firstGallery, active)) {
      moveFocus(galleryFromRef.current || lastColumn);

      return;
    }

    if (!event.shiftKey && firstGallery && lastColumn && lastColumn !== lastGallery && controlContains(lastColumn, active)) {
      galleryFromRef.current = lastColumn;
      moveFocus(firstGallery);

      return;
    }

    if (!event.shiftKey && lastGallery && controlContains(lastGallery, active)) {
      const trigger = galleryFromRef.current;
      const triggerIndex = trigger
        ? columnTabs.findIndex((el) => el === trigger || el.contains(trigger))
        : -1;
      const nextColumn = triggerIndex >= 0 ? columnTabs[triggerIndex + 1] : null;

      galleryFromRef.current = null;
      moveFocus(nextColumn || nextL1 || firstPromo || firstChrome);

      return;
    }

    if (!event.shiftKey && controlContains(lastL2, active)) {
      moveFocus(nextL1 || firstPromo || firstChrome);

      return;
    }

    if (event.shiftKey && controlContains(firstL2, active)) {
      moveFocus(selectedL1);

      return;
    }

    if (event.shiftKey && !nextL1 && controlContains(firstPromo, active)) {
      moveFocus(lastL2);
    }
  }, [categories, selectedCategoryId]);

  const previewGallery = useCallback((images) => {
    setIsGalleryPreview(true);
    setHoveredGalleryImages(Array.isArray(images) && images.length ? images : null);
  }, []);

  const hideGalleryPreview = useCallback(() => {
    galleryFromRef.current = null;
    setIsGalleryPreview(false);
    setHoveredGalleryImages(null);
  }, []);

  useEffect(() => {
    hideGalleryPreview();
  }, [hideGalleryPreview, selectedCategoryId, navDrawerTabId]);

  useEscapeToClose(isOpen, closeNavDrawer);

  useLayoutEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      setStaggerL1(false);

      return undefined;
    }

    if (!wasOpenRef.current) {
      setStaggerL1(true);
      wasOpenRef.current = true;
      const timeoutId = window.setTimeout(() => setStaggerL1(false), 600);

      return () => window.clearTimeout(timeoutId);
    }

    setStaggerL1(false);

    return undefined;
  }, [isOpen, navDrawerTabId]);

  useEffect(() => {
    return () => {
      if (hoverIntentTimerRef.current) {
        clearTimeout(hoverIntentTimerRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return undefined;
    }

    const headerEl = document.querySelector('[data-testid="header"]');
    const root = document.documentElement;

    const syncDrawerTop = () => {
      if (!headerEl) {
        root.style.setProperty('--nav-drawer-top', 'var(--header-height, 72px)');
        root.style.setProperty('--nav-drawer-left', '0px');
        root.style.setProperty('--nav-header-height', '72px');

        return;
      }

      const headerBox = headerEl.getBoundingClientRect();
      const contentEl = headerEl.querySelector('.header-content-wrapper') || headerEl.firstElementChild;
      const contentLeft = contentEl ? contentEl.getBoundingClientRect().left : 0;

      root.style.setProperty('--nav-drawer-top', `${Math.max(0, Math.round(headerBox.top))}px`);
      root.style.setProperty('--nav-drawer-left', `${Math.max(0, Math.round(contentLeft))}px`);
      root.style.setProperty('--nav-header-height', `${Math.round(headerBox.height)}px`);
    };

    syncDrawerTop();
    window.addEventListener('scroll', syncDrawerTop, {passive: true});
    window.addEventListener('resize', syncDrawerTop);

    return () => {
      window.removeEventListener('scroll', syncDrawerTop);
      window.removeEventListener('resize', syncDrawerTop);
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const drawerChrome = (
    <Flex className="mega-menu-drawer__chrome" data-nav-chrome="" {...styles.drawerChrome}>
      <NavigationPrimaryTabs
        activeTabId={navDrawerTabId}
        tabs={primaryTabs}
        tone="drawer"
        onTabClick={handleTabClick}
      />
      <IconButton
        aria-label={intl.formatMessage({
          defaultMessage: 'Search',
          id: 'header.button.assistive_msg.search',
        })}
        aria-pressed={isSearchTab}
        className={`mega-menu-drawer__search-tab${isSearchTab ? ' mega-menu-drawer__search-tab--active' : ''}`}
        fontSize="20px"
        icon={<SearchIcon aria-hidden boxSize="20px" h="20px" viewBox="0 0 20 20" w="20px" />}
        variant="unstyled"
        {...styles.drawerSearchTab}
        {...(isSearchTab ? styles.drawerSearchTabActive : {})}
        alignItems="center"
        boxSize="32px"
        display="flex"
        h="32px"
        justifyContent="center"
        minH="32px"
        minW="32px"
        p={0}
        w="32px"
        onClick={() => openNavDrawer(SEARCH_TAB_ID)}
      />
      <Box
        as="button"
        type="button"
        aria-label={intl.formatMessage({
          defaultMessage: 'Close navigation',
          id: 'header.nav.close',
        })}
        className="mega-menu-drawer__close"
        {...styles.drawerClose}
        alignItems="center"
        color="brandColors.primaryText"
        display="flex"
        h="32px"
        justifyContent="center"
        lineHeight={0}
        minH="32px"
        minW="32px"
        p={0}
        w="32px"
        onClick={closeNavDrawer}
      >
        <HeaderCloseIcon
          aria-hidden
          boxSize="20px"
          h="20px"
          viewBox="0 0 20 20"
          w="20px"
        />
      </Box>
    </Flex>
  );

  return (
    <Box
      aria-label={intl.formatMessage({
        defaultMessage: 'Site navigation',
        id: 'header.nav.drawer_aria',
      })}
      aria-modal="true"
      className="mega-menu-drawer"
      data-nav-stagger={staggerL1 ? '' : undefined}
      role="dialog"
      {...styles.drawer}
      maxW="100vw"
      w={
        selectedCategory && galleryImages.length > 0
          ? '100vw'
          : `calc(${drawerWidth} + var(--nav-drawer-left, 0px))`
      }
      {...props}
      onKeyDownCapture={handleDrawerTabOrder}
    >
      <FocusLock
        autoFocus={false}
        className="mega-menu-drawer__lock"
        returnFocus
        style={{
          display: 'flex',
          flex: '1 1 auto',
          flexDirection: 'column',
          height: '100%',
          maxHeight: '100%',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {isSearchTab ? (
          <>
            {drawerChrome}
            <Box className="mega-menu-drawer__search" {...styles.drawerSearchBody}>
              <Search onCloseModal={closeNavDrawer} />
            </Box>
          </>
        ) : (
          <Flex
            className="mega-menu-drawer__body"
            {...styles.drawerBody}
          >
            <Box className="mega-menu-drawer__l1-column" {...styles.drawerL1Column}>
              {drawerChrome}
              <Box className="mega-menu-drawer__l1-scroll" {...styles.drawerL1Scroll}>
              <Box
                as="ul"
                aria-busy={isLoading && categories.length === 0 ? true : undefined}
                className="mega-menu-drawer__l1"
                listStyleType="none"
                {...styles.drawerL1}
              >
              {isLoading && categories.length === 0 ?
                [0, 1, 2, 3].map((i) => (
                  <Box as="li" key={`l1-pending-${i}`} {...styles.drawerL1Item}>
                    <Skeleton height="24px" maxW="220px" width="70%" />
                  </Box>
                )) :
                categories.map((item, index) => {
                const id = getMenuItemId(item);
                const isSelected = id === selectedCategoryId;
                const hasChildren = menuItemHasSubmenu(item);
                const featuredLink = getFeaturedLink(item);
                const href = getLinkUrl(featuredLink);

                return (
                  <Box
                    as="li"
                    key={id || getMenuItemTitle(item)}
                    {...styles.drawerL1Item}
                    style={{'--nav-stagger-index': index}}
                  >
                    <Button
                      as={href ? BaseLink : 'button'}
                      aria-controls={hasChildren ? 'mega-menu-drawer-l2' : undefined}
                      aria-expanded={hasChildren ? isSelected : undefined}
                      data-nav-l1-id={id}
                      {...getNavLinkAnchorProps(featuredLink, href)}
                      variant="unstyled"
                      {...styles.drawerL1Button}
                      {...(isSelected ? styles.drawerL1ButtonSelected : {})}
                      {...(!isSelected && selectedCategoryId ? styles.drawerL1ButtonMuted : {})}
                      onClick={(event) => handleCategoryClick(item, event)}
                      onFocus={() => handleCategoryFocus(item)}
                      onMouseEnter={() => handleCategoryMouseEnter(item)}
                      onMouseLeave={handleCategoryMouseLeave}
                    >
                      <Text
                        as="span"
                        fontFamily="body"
                        fontSize="20px"
                        fontWeight={500}
                        letterSpacing={0}
                        lineHeight="1.2"
                        sx={{fontFeatureSettings: "'lnum' 1, 'pnum' 1"}}
                      >
                        {getMenuItemTitle(item)}
                      </Text>
                      {hasChildren ? (
                        <ChevronRightIcon2
                          aria-hidden
                          boxSize="unset"
                          className="mega-menu-drawer__l1-chevron"
                          color="brandColors.greyscale"
                          height="11.25px"
                          minW="5.47px"
                          style={{width: '5.47px', height: '11.25px', minWidth: '5.47px', flexShrink: 0}}
                          width="5.47px"
                        />
                      ) : null}
                    </Button>
                  </Box>
                );
              })}
              </Box>
              {promotionalLinks.length > 0 ? (
                <Box
                  as="ul"
                  className="mega-menu-drawer__promo"
                  {...styles.drawerPromoGroup}
                >
                  {promotionalLinks.map((link, index) => {
                    const href = getLinkUrl(link);
                    const label = getLinkText(link);

                    if (!label) {
                      return null;
                    }

                    return (
                      <Box
                        as="li"
                        key={link?.sys?.id || href || label}
                        listStyle="none"
                        style={{'--nav-stagger-index': categories.length + index}}
                      >
                        {href ? (
                          <BaseLink
                            {...getNavLinkAnchorProps(link, href)}
                            {...styles.drawerPromoLink}
                            onClick={handleNavigate}
                          >
                            {label}
                          </BaseLink>
                        ) : (
                          <Text as="span" {...styles.drawerPromoLink}>{label}</Text>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ) : null}
              </Box>
            </Box>
            {selectedCategory ? (
                <Flex
                alignItems="stretch"
                alignSelf="stretch"
                flex="1 1 auto"
                h="100%"
                id="mega-menu-drawer-l2"
                key={selectedCategoryId}
                maxH="100%"
                minH={0}
                minW={0}
                overflow="hidden"
                sx={{
                  animation: staggerL1 ? undefined : 'navL2CrossFade 180ms ease both',
                  '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                  },
                }}
              >
                <LevelTwoColumns
                  category={selectedCategory}
                  categoryImages={categoryImages}
                  styles={styles}
                  onHideGallery={hideGalleryPreview}
                  onNavigate={handleNavigate}
                  onPreviewImages={previewGallery}
                />
                {galleryImages.length > 0 ? (
                  <CategoryGallery
                    images={galleryImages}
                    styles={styles}
                    onNavigate={handleNavigate}
                  />
                ) : null}
              </Flex>
            ) : null}
          </Flex>
        )}
      </FocusLock>
    </Box>
  );
};

MegaMenuDrawer.displayName = 'MegaMenuDrawer';
MegaMenuDrawer.propTypes = {
  isLoading: PropTypes.bool,
  primaryTabs: PropTypes.array,
};
